import { useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import GeoContext from 'contexts/geoContext'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import config from 'constants/config'
import { useHotelTrendingDestinations } from 'home/hooks/useHotelTrendingDestinations'
import { useShowLocalDestinationOfferCarousel } from 'home/hooks/useShowLocalDestinationOfferCarousel'
import {
  buildSortedTrendingDestinationFilterChips,
  DEFAULT_TRENDING_DESTINATIONS_FILTER,
} from 'home/components/TrendingDestinations/trendingDestinationsSectionUtils'

interface Options {
  defaultFilter?: App.DestinationSelectedFilters
  filterOrder?: Record<App.DestinationSelectedFilters, number>
}

export function useTrendingDestinationsSectionData(options: Options = {}) {
  const {
    defaultFilter = DEFAULT_TRENDING_DESTINATIONS_FILTER,
    filterOrder,
  } = options
  const [selectedFilter, setSelectedFilter] = useState<App.DestinationSelectedFilters>(defaultFilter)
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const leUserId = useAppSelector(getCurrentUserId)
  const lereDestinationModelEnabled = useFeatureFlag(OptimizelyFeatureFlags.lereDestinationModelEnabled)

  const sortedFilters = useMemo(() => {
    return buildSortedTrendingDestinationFilterChips(currentRegionCode, intl, filterOrder)
  }, [currentRegionCode, filterOrder, intl])

  const isHotelFilter = selectedFilter === 'hotel'
  const disableLerePersonalisation = !(lereDestinationModelEnabled && isHotelFilter && config.BRAND == 'luxuryescapes')
  const [homepageTrendingDestinations, fetchingDestinations] = useHotelTrendingDestinations(
    selectedFilter,
    currentRegionCode,
    leUserId,
    domainUserId,
    disableLerePersonalisation,
  )

  const showLocalDestinationOfferCarousel = useShowLocalDestinationOfferCarousel({
    isHotelFilter,
    regionCode: currentRegionCode,
  })

  const searchMapLink = `/search/map?adults=2&children=none&destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}`

  return {
    selectedFilter,
    setSelectedFilter,
    sortedFilters,
    currentRegionCode,
    fetchingDestinations,
    homepageTrendingDestinations,
    showLocalDestinationOfferCarousel,
    searchMapLink,
    intl,
  }
}
